// One queue for the saves the buyer makes by leaving a field (Lane 1440): the RFP title,
// launch date and offers due on the Overview, and each supplier's contact on the
// Suppliers tab. Saves run one at a time in the order they were made. "Launch" waits for
// the queue and refuses to go on while a save failed (Codex plan review r1 #3), so a
// supplier is never invited with a detail the buyer typed but the server never saved.
import { useCallback, useMemo, useRef, useState } from 'react';

export default function useSaveQueue() {
  const queue = useRef(Promise.resolve());
  const failed = useRef(new Map());        // key -> message of its last failed save
  const pending = useRef(0);
  // What the buyer typed and is still being saved, kept here (not in a tab) so it survives
  // a tab change while its save runs (Codex code r2 #2): contacts by supplier.
  const memory = useRef({ typed: {}, saving: {}, savedAt: {} });
  const [state, setState] = useState({ pending: 0, error: '' });

  const publish = useCallback(() => {
    const errors = Array.from(failed.current.values());
    setState({ pending: pending.current, error: errors[0] || '' });
  }, []);

  // `key` names the field, or several (a later good save of the same field clears its
  // failure; nothing else does), or {fail, ok}: the keys a failure leaves and the keys a
  // success clears (a mark clears every field it writes, Codex code r4).
  // Resolves true when the save worked, false when it failed (the message is kept).
  const run = useCallback((key, fn) => {
    const plain = (k) => (Array.isArray(k) ? k : [k]);
    const failKeys = key && key.fail ? plain(key.fail) : plain(key);
    const okKeys = key && key.ok ? plain(key.ok) : failKeys;
    pending.current += 1;
    publish();
    const step = queue.current.then(async () => {
      try {
        await fn();
        okKeys.forEach((k) => failed.current.delete(k));
        return true;
      } catch (e) {
        failKeys.forEach((k) => failed.current.set(k, e && e.message ? e.message : 'Not saved. Try again.'));
        return false;
      } finally {
        pending.current -= 1;
        publish();
      }
    });
    queue.current = step.then(() => undefined);
    return step;
  }, [publish]);

  // Every save made so far finished; true when none of them is left failed.
  const flush = useCallback(async () => {
    await queue.current;
    return failed.current.size === 0;
  }, []);

  const clear = useCallback((key) => { failed.current.delete(key); publish(); }, [publish]);

  return useMemo(() => ({ run, flush, clear, memory, pending: state.pending, error: state.error }),
    [run, flush, clear, state]);
}
